import React, { useEffect, useState } from "react";
import Card from "components/card";
import {
  MdAdminPanelSettings,
  MdOutlineSupportAgent,
  MdRemoveRedEye,
  MdDeleteForever,
} from "react-icons/md";
import { FaUserTie } from "react-icons/fa";
import { LiaUserEditSolid } from "react-icons/lia";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { fetchData, deleteData } from "../../../../services/ApiFetching";
import { useNavigate } from "react-router-dom";

const columnHelper = createColumnHelper();

function UserTable({ setCreateUser, setUserId }) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState([]);
  const status = { active: "Active", inactive: "In Active"};

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const users = await fetchData("users");
        const transformed = users.map((user) => ({
          id: user.id,
          name: user.username,
          email: user.email,
          role: user.role,
          status: user.status,
        }));
        setData(transformed);
      } catch (error) {
        console.error("Error fetching users:", error.message);
      }
    };

    fetchUsers();
  }, []);

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">NAME</p>,
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("role", {
      id: "role",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">ROLE</p>
      ),
      cell: (info) => {
        const role = info.getValue();
        if (role === "admin") {
          return (
            <div className="flex items-center gap-2 text-[15px] text-gray-600 dark:text-white">
              <MdAdminPanelSettings size={25} /> Admin
            </div>
          );
        } else if (role === "agent") {
          return (
            <div className="flex items-center gap-2 text-[15px] text-gray-600 dark:text-white">
              <MdOutlineSupportAgent size={25} /> Agent
            </div>
          );
        } else if (role === "customer") {
          return (
            <div className="flex items-center gap-2 text-[15px] text-gray-600 dark:text-white">
              <FaUserTie size={25} /> Customer
            </div>
          );
        } else {
          return (
            <p className="text-sm text-gray-600 dark:text-white capitalize">
              {role}
            </p>
          );
        }
      },
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">EMAIL ID</p>,
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">STATUS</p>,
      cell: (info) => {
        const value = info.getValue();
        return (
          <p className="text-sm font-bold text-navy-700 dark:text-white">
          {status[value] || info.getValue() }
        </p>
        )
      },
    }),
    columnHelper.accessor("action", {
      id: "action",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">ACTION</p>
      ),
      cell: (info) => {
        const userId = info.row.original.id;
        return (
          <div className="flex items-center gap-3">
            <p className="text-lg text-gray-600 hover:text-brand-600 font-bold dark:text-white cursor-pointer"
              onClick={() => { navigate(`/admin/users/${userId}`) }} >
              <MdRemoveRedEye size={25} />
            </p>
            <p
              className="text-lg text-gray-600 hover:text-brand-600 font-bold dark:text-white cursor-pointer"
              onClick={() => handleEdit(userId)}
            >
              <LiaUserEditSolid size={25} />
            </p>
            <p
              className="text-lg text-gray-600 hover:text-red-600 font-bold dark:text-white cursor-pointer"
              onClick={() => handleDelete(userId)}
            >
              <MdDeleteForever size={25} />
            </p>
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleDelete = async (deleteUserId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await deleteData(`users/${deleteUserId}`);
      setData((prev) => prev.filter((user) => user.id !== deleteUserId));
    } catch (err) {
      console.error("Delete failed:", err.message);
    }
  };

  const handleEdit = async (userId) => {
    setCreateUser(true);
    setUserId(userId);
  };


  return (
    <Card extra={"w-full h-full sm:overflow-auto px-6"}>
      <header className="relative flex items-center justify-between pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">ALL USERS</div>
        <button onClick={() => { setCreateUser(true); setUserId("") }}
          className="w-fit linear rounded-full bg-brand-600 px-4 py-3 text-xl font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90">
          Create User
        </button>
      </header>

      <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
        <div className="max-h-[75vh] overflow-y-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="!border-px !border-gray-400">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start"
                    >
                      <div className="items-center justify-between text-xs text-gray-200">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: "",
                          desc: "",
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="min-w-[150px] border-white/0 py-3 pr-4"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}

export default UserTable;
